import {EcgStatement, StatementLanguage, StatementSectionId, STATEMENT_SECTION_ORDER} from "./types"
import {GENERATED_STATEMENTS} from "./catalogue.generated"
import {CUSTOM_STATEMENTS} from "./catalogue.custom"
import {BACKEND_SHORTCUTS} from "./shortcuts"

// `export type {...}` needs TypeScript 3.8 syntax that this project's Babel
// (react-scripts 3.3) cannot parse, so the whole module is re-exported instead.
export * from "./types"

/**
 * The catalogue the screen works from: the backend's own statements, then the
 * ones written for this app because the backend has no key for them.
 *
 * Both halves are tagged with where their wording came from. The tag is set
 * HERE rather than in the two files, so it cannot be set wrongly by hand and
 * cannot drift: whatever is in `catalogue.custom.ts` is local by definition,
 * and whatever the generator wrote is the backend's text by definition.
 *
 * Generated first, so that when two statements score equally the backend's
 * wording — the wording the printed report uses — is the one offered first.
 */
export const ECG_STATEMENTS: EcgStatement[] = [
    ...GENERATED_STATEMENTS.map(statement => ({
        ...statement,
        origin: "backend" as const,
        // attaching codes changes no backend TEXT; it only adds ways to reach
        // it, and it has to happen here because the generated file is rewritten
        shortcuts: BACKEND_SHORTCUTS[statement.key],
    })),
    ...CUSTOM_STATEMENTS.map(statement => ({...statement, origin: "local" as const})),
];

/**
 * Matching and insertion for the interpretation statement catalogue.
 *
 * Everything here is pure and works on plain strings, so the behaviour that
 * decides what a technician's keystroke does can be tested without mounting a
 * component — see ./index.test.ts.
 *
 * The one rule that shapes the rest: a findings box holds ONE STATEMENT PER
 * LINE. That is how the algorithm writes it, how the report prints it, and it
 * is why matching looks only at the line the caret is on. Without that rule the
 * suggestions would have to guess where a phrase starts inside a paragraph.
 */

/** Below this many characters a query matches too much to be useful. */
export const MIN_QUERY_LENGTH = 2;

/** Suggestions shown at once. Enough to choose from, few enough to scan. */
export const MAX_SUGGESTIONS = 8;

/** Where the lead names go in a statement that has a slot. */
const LEAD_SLOT = "%s";

const LANGUAGES: StatementLanguage[] = ["en", "it", "uk"];

/**
 * The catalogue language for an i18next language tag.
 *
 * i18next reports tags like `en-GB`, and the app's Ukrainian is `uk` in code
 * but shown as "UA" in the header. Anything unrecognised reads English, which
 * is the catalogue's own fallback.
 *
 * @param tag
 */
export function statementLanguage(tag: string | undefined | null): StatementLanguage {
    const base = String(tag || "").toLowerCase().split(/[-_]/)[0];
    const match = LANGUAGES.find(language => language === base);

    return match === undefined ? "en" : match
}

export type StatementParts = {
    /** Everything before the lead slot. */
    prefix: string
    /** Everything after it — usually empty, but Italian ends in a full stop. */
    suffix: string
}

/**
 * A statement split around its lead slot.
 *
 * This split is the whole reason lead statements work in every language. The
 * English catalogue reads "ST elevation in %s", so dropping the slot leaves a
 * clean "ST elevation in ". The Italian reads "Sopra-slivellamento ST in %s."
 * — the full stop comes AFTER the leads. Treating the text as one string would
 * have written "Sopra-slivellamento ST in ." and left the reviewer typing the
 * leads after the full stop.
 *
 * @param statement
 * @param language
 */
export function statementParts(statement: EcgStatement, language: StatementLanguage): StatementParts {
    const raw = statement.text[language];
    const slot = raw.indexOf(LEAD_SLOT);

    if (slot === -1) {
        return {prefix: raw, suffix: ""}
    }

    return {prefix: raw.slice(0, slot), suffix: raw.slice(slot + LEAD_SLOT.length)}
}

/**
 * The statement with the slot removed — what a finished line reads like minus
 * its leads. Used for matching and for comparing against text already written.
 *
 * @param statement
 * @param language
 */
export function statementText(statement: EcgStatement, language: StatementLanguage): string {
    const {prefix, suffix} = statementParts(statement, language);

    return prefix + suffix
}

/**
 * How the statement is shown in a list.
 *
 * A lead statement shows only its prefix, trimmed: "Sopra-slivellamento ST in"
 * rather than "Sopra-slivellamento ST in .", which reads as a typo. The list
 * marks these with "+ leads" anyway, so nothing is lost.
 *
 * @param statement
 * @param language
 */
export function statementLabel(statement: EcgStatement, language: StatementLanguage): string {
    const {prefix, suffix} = statementParts(statement, language);

    return statement.leads ? prefix.trim() : prefix + suffix
}

/**
 * Case- and accent-insensitive form used for every comparison.
 *
 * Italian carries accents the technician may not type ("perché"), and the same
 * statement can arrive from the algorithm with different spacing.
 *
 * @param value
 */
export function normalise(value: string): string {
    return value
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim()
}

/**
 * Shortest word a query may match on, unless it is the statement's first word.
 *
 * Without this, typing the word "in" matches "ST depression in %s", "Q wave in
 * %s", "Inverted T wave in %s" and the rest, and the list fills with noise the
 * moment anyone writes an ordinary sentence. Four is the length that keeps the
 * useful mid-statement hits — "fibrillation", "hemiblock", "ectopic" — while
 * dropping the joining words. The first word is always allowed, so "st" still
 * finds every ST statement.
 */
const MIN_INNER_WORD_LENGTH = 4;

/**
 * Is this word an abbreviation rather than an ordinary short word?
 *
 * The catalogue writes its abbreviations in capitals — "ST", "QT", "QRS",
 * "PR", "WPW" — and its joining words in lower case. That is the whole test:
 * it needs no list to maintain, and it works in every language the catalogue
 * carries, because Cyrillic has cases too.
 *
 * @param word a word from the ORIGINAL text, not the normalised form
 */
function isAbbreviation(word: string): boolean {
    // keep only characters that HAVE a case, which drops digits, hyphens and
    // punctuation in any script — `\p{L}` would need an ES6 target, and this
    // project compiles to ES5
    const letters = word
        .split("")
        .filter(character => character.toLowerCase() !== character.toUpperCase())
        .join("");

    return letters.length >= 2 && letters === letters.toUpperCase()
}

/**
 * Does a word in the statement begin with the query?
 *
 * Takes the ORIGINAL text rather than the normalised form, because the
 * capitalisation is what tells an abbreviation from a joining word, and
 * normalising throws it away.
 *
 * @param text statement text as written
 * @param needle normalised query
 */
function matchesWord(text: string, needle: string): boolean {
    return text
        .split(/\s+/)
        .some((word, index) => {
            const plain = normalise(word);

            if (!plain.startsWith(needle)) {
                return false
            }

            // the first word is always allowed, and a long enough word carries
            // meaning on its own — "fibrillation", "hemiblock", "ectopic"
            if (index === 0 || plain.length >= MIN_INNER_WORD_LENGTH) {
                return true
            }

            // a SHORT word inside the sentence: "in" and "di" are noise, but
            // "ST", "QT" and "QRS" are the finding itself. Without this,
            // typing "qt" matched nothing at all despite two QT statements,
            // and "st" reached only two of the four ST ones.
            return isAbbreviation(word)
        })
}

/** The part of a statement key a technician might type, e.g. `LAFB`. */
function keyTail(statement: EcgStatement): string {
    const parts = statement.key.split(".");

    return normalise(parts[parts.length - 1])
}

/** Score for the statement's declared abbreviations, or 0 if none of them hit. */
function shortcutScore(statement: EcgStatement, needle: string): number {
    const shortcuts = statement.shortcuts;

    if (typeof shortcuts === "undefined") {
        return 0
    }

    let best = 0;

    for (const shortcut of shortcuts) {
        const code = normalise(shortcut);

        if (code === needle) {
            // an exact code beats everything: someone typing "wpw" has already
            // decided, and should not have to scroll past statements that
            // merely contain those letters
            return 5
        }

        if (code.startsWith(needle)) {
            // HALF-TYPED code, and deliberately weaker than a statement whose
            // text starts with the same letters. "st" is the case that proves
            // it: `stach` begins with those letters, and ranking a half-typed
            // code above real text buried ST elevation and ST depression under
            // sinus tachycardia for the commonest query in the catalogue.
            best = 2.5
        }
    }

    return best
}

export type StatementMatch = {
    statement: EcgStatement
    /** Text that would be inserted, lead slot already removed. */
    text: string
    /**
     * What the list should SHOW, which is not always what gets inserted.
     *
     * An Italian lead statement inserts as "Sopra-slivellamento ST in ." —
     * the full stop belongs after the leads — but showing that in a list puts
     * a stranded full stop on screen that reads as a typo. The label drops it;
     * the "+ leads" pill already says the line is unfinished.
     */
    label: string
    /** Higher is a better match; used for ordering only. */
    score: number
}

/**
 * Statements matching what the technician has typed so far.
 *
 * Five ways to hit, strongest first:
 *
 * 1. a declared abbreviation in full — "wpw" -> Ventricular pre-excitation,
 *    "2:1" -> both the flutter and the AV block, which is the honest answer
 * 2. the statement starts with the query — "atrial fib" -> Atrial fibrillation
 * 3. a declared abbreviation half-typed — ranked BELOW real text on purpose
 * 4. a word inside it starts with the query — "fib" -> Atrial fibrillation
 * 5. the catalogue key starts with it — "lafb" -> Left anterior hemiblock
 *
 * The fourth is what gives short codes for free for the BACKEND statements:
 * `LAFB`, `SVEB`, `RBBB` and the rest are already the backend's own key names,
 * so no list of abbreviations has to be maintained for them. The statements
 * written for this app have descriptive keys instead, so they carry their
 * abbreviations explicitly — see `shortcuts` in `catalogue.custom.ts`.
 *
 * Nothing here expands on its own: a shortcut only reorders the suggestions,
 * and the text is inserted when the technician chooses it. That is what makes
 * it safe to let "at" or "mi" be codes at all.
 *
 * A plain substring hit does NOT match: "in" would otherwise pull in half the
 * catalogue through "ST depression in".
 *
 * @param query the text the caret sits in
 * @param language
 * @param limit
 */
export function matchStatements(
    query: string,
    language: StatementLanguage,
    limit: number = MAX_SUGGESTIONS,
): StatementMatch[] {
    const needle = normalise(query);

    if (needle.length < MIN_QUERY_LENGTH) {
        return []
    }

    const matches: StatementMatch[] = [];

    ECG_STATEMENTS.forEach((statement, order) => {
        const text = statementText(statement, language);
        const haystack = normalise(text);
        let score = 0;

        if (haystack.startsWith(needle)) {
            score = 3
        } else if (matchesWord(text, needle)) {
            score = 2
        } else if (keyTail(statement).startsWith(needle)) {
            score = 1
        }

        // the BEST of the two routes, never the first one found: `std` is a
        // code for ST depression, and letting that half-match (2.5) stand in
        // for the statement's own text (3) dropped it below sinus tachycardia
        score = Math.max(score, shortcutScore(statement, needle));

        if (score > 0) {
            // order keeps the catalogue's own sequence stable within a score
            matches.push({
                statement,
                text,
                label: statementLabel(statement, language),
                score: (score * 1000) - order,
            })
        }
    });

    return matches.sort((a, b) => b.score - a.score).slice(0, limit)
}

export type LineRange = {
    /** Index of the first character of the line. */
    start: number
    /** Index one past the last character of the line. */
    end: number
    text: string
}

/**
 * The line the caret sits on.
 *
 * @param value the whole field
 * @param caret selectionStart
 */
export function lineAt(value: string, caret: number): LineRange {
    const safeCaret = Math.max(0, Math.min(value.length, caret));
    const start = value.lastIndexOf("\n", safeCaret - 1) + 1;
    const nextBreak = value.indexOf("\n", safeCaret);
    const end = nextBreak === -1 ? value.length : nextBreak;

    return {start, end, text: value.slice(start, end)}
}

export type Insertion = {
    value: string
    /** Where the caret lands: at the lead slot, or on the next empty line. */
    caret: number
}

/**
 * Replace the caret's line with a statement.
 *
 * A statement WITHOUT leads is a finished line, so it gets a newline after it
 * and the caret moves to the next line, ready for the next finding. A statement
 * WITH leads is unfinished — the caret stays at the end of "ST elevation in "
 * so the lead names can be typed immediately, and no newline is added, because
 * adding one would put the leads on the wrong line.
 *
 * @param value the whole field
 * @param caret selectionStart
 * @param statement
 * @param language
 * @param maxLength refuses rather than truncating past the field's limit
 */
export function insertStatement(
    value: string,
    caret: number,
    statement: EcgStatement,
    language: StatementLanguage,
    maxLength: number = Infinity,
): Insertion | null {
    const line = lineAt(value, caret);
    const {prefix, suffix} = statementParts(statement, language);
    const before = value.slice(0, line.start);
    const after = value.slice(line.end);

    /*
     * A finished statement leaves the caret on an empty line, ready for the
     * next finding: the line after it when that one is empty, otherwise a new
     * one opened under it. Leaving the caret at the end of the statement made
     * the next finding run straight on into it.
     *
     * A statement that still needs its leads is not closed off at all.
     */
    const followedByBreak = after.startsWith("\n");
    const nextLineEmpty = followedByBreak && (after.length === 1 || after.charAt(1) === "\n");
    const lineBreak = statement.leads || nextLineEmpty ? "" : "\n";
    const next = before + prefix + suffix + lineBreak + after;

    if (next.length > maxLength) {
        return null
    }

    // the caret lands in the slot itself, BEFORE any trailing punctuation, so
    // the leads are typed inside the sentence rather than after it
    const caretOffset = statement.leads
        ? prefix.length
        // past the line break: the one added, or the empty line already there
        : prefix.length + suffix.length + 1;

    return {value: next, caret: before.length + caretOffset}
}

/**
 * Is this statement already written in the field?
 *
 * Compared line by line rather than with a substring search, so "Atrial
 * flutter" is not reported as present merely because "Atrial fibrillation" is.
 * A statement with leads counts as present once its stem is there, whatever
 * leads follow.
 *
 * @param value
 * @param statement
 * @param language
 */
export function containsStatement(value: string, statement: EcgStatement, language: StatementLanguage): boolean {
    const text = normalise(statementText(statement, language));

    if (text === "") {
        return false
    }

    return value.split("\n").some(line => {
        const candidate = normalise(line);

        return statement.leads ? candidate.startsWith(text) : candidate === text
    })
}

/**
 * The twelve leads, in the order a report names them.
 *
 * Kept in this order whatever order they are tapped in, because "II, III, aVF"
 * is how an inferior territory is written and "aVF, II, III" is not.
 */
export const LEAD_NAMES: string[] = [
    "I", "II", "III", "aVR", "aVL", "aVF", "V1", "V2", "V3", "V4", "V5", "V6",
];

export type LeadLine = {
    /** Index of the first character of the line. */
    start: number
    /** Index one past the last character of the line. */
    end: number
    /** The statement text up to the lead slot, as written on the line. */
    stem: string
    /** Anything the sentence ends with after the leads, e.g. Italian's full stop. */
    tail: string
    /** The lead text exactly as written, including anything unrecognised. */
    raw: string
    /** Leads already written, in the order they appear. */
    leads: string[]
    /**
     * False when the line holds something the chips did not write — a range
     * like "V2-V4", or a note. The chips then only append, and never rewrite
     * what a person typed.
     */
    recognised: boolean
}

/**
 * Is the caret sitting on a statement that still wants its leads?
 *
 * Works on the TEXT, not on what was last inserted, so it is equally true of a
 * line the algorithm prefilled, one inserted from the palette, and one typed by
 * hand — click into any of them and the chips are there.
 *
 * @param value the whole field
 * @param caret selectionStart
 * @param language
 */
export function leadLineAt(value: string, caret: number, language: StatementLanguage): LeadLine | null {
    const line = lineAt(value, caret);
    const candidates = ECG_STATEMENTS
        .filter(statement => statement.leads)
        .map(statement => statementParts(statement, language))
        // longest first, so "Non-significant ST elevation in " wins over a
        // shorter prefix that happens to be a suffix of it
        .sort((a, b) => b.prefix.length - a.prefix.length);

    const parts = candidates.find(candidate => normalise(line.text).startsWith(normalise(candidate.prefix)));

    if (parts === undefined) {
        return null
    }

    // take the stem from the line itself, so the reviewer's own spacing stands
    const written = line.text.slice(0, parts.prefix.length);
    let rest = line.text.slice(parts.prefix.length).trim();

    // drop the sentence's own tail before reading the leads, so Italian's
    // trailing full stop is not mistaken for a lead name
    if (parts.suffix !== "" && rest.endsWith(parts.suffix.trim())) {
        rest = rest.slice(0, rest.length - parts.suffix.trim().length).trim()
    }

    const tokens = rest === "" ? [] : rest.split(/[,;]+/).map(part => part.trim()).filter(part => part !== "");
    const known = tokens.map(token => LEAD_NAMES.find(lead => lead.toLowerCase() === token.toLowerCase()));

    return {
        start: line.start,
        end: line.end,
        stem: written,
        tail: parts.suffix,
        /** Everything between the stem and the tail, exactly as written. */
        raw: rest,
        leads: known.filter((lead): lead is string => typeof lead === "string"),
        recognised: known.every(lead => typeof lead === "string"),
    }
}

/**
 * Rewrite a lead line with the given leads.
 *
 * @param value the whole field
 * @param line from {@link leadLineAt}
 * @param leads names to write, filtered and ordered against LEAD_NAMES
 * @param maxLength refuses rather than truncating past the field's limit
 */
export function setLeads(
    value: string,
    line: LeadLine,
    leads: string[],
    maxLength: number = Infinity,
): Insertion | null {
    const ordered = LEAD_NAMES.filter(lead => leads.indexOf(lead) !== -1);
    const text = line.stem + ordered.join(", ") + line.tail;
    const next = value.slice(0, line.start) + text + value.slice(line.end);

    if (next.length > maxLength) {
        return null
    }

    // caret before the sentence tail, so typing continues inside the sentence
    return {value: next, caret: line.start + text.length - line.tail.length}
}

/**
 * Add one lead to a line the chips did not write.
 *
 * Used when {@link LeadLine.recognised} is false — the line holds something
 * the chips cannot model, a range like "V2-V4" or a note. Rebuilding the line
 * from the recognised names would delete that, so this appends instead and
 * leaves every character the reviewer typed exactly where it was.
 *
 * @param value the whole field
 * @param line from {@link leadLineAt}
 * @param lead the lead to add
 * @param maxLength refuses rather than truncating past the field's limit
 */
export function appendLead(
    value: string,
    line: LeadLine,
    lead: string,
    maxLength: number = Infinity,
): Insertion | null {
    const written = line.raw
        .split(/[,;]+/)
        .map(part => part.trim().toLowerCase())
        .filter(part => part !== "");

    // already there in some form: adding it twice helps nobody
    if (written.indexOf(lead.toLowerCase()) !== -1) {
        return null
    }

    const separator = line.raw === "" ? "" : ", ";
    const text = line.stem + line.raw + separator + lead + line.tail;
    const next = value.slice(0, line.start) + text + value.slice(line.end);

    if (next.length > maxLength) {
        return null
    }

    return {value: next, caret: line.start + text.length - line.tail.length}
}

/**
 * Statements grouped for the palette, in report order, honouring a search box.
 *
 * An empty query returns every section, which is what makes the palette a
 * browsable list rather than only a search.
 *
 * @param query
 * @param language
 */
export function groupedStatements(
    query: string,
    language: StatementLanguage,
): {section: StatementSectionId, statements: EcgStatement[]}[] {
    const needle = normalise(query);
    const visible = needle === ""
        ? ECG_STATEMENTS
        : ECG_STATEMENTS.filter(statement =>
            normalise(statementText(statement, language)).includes(needle)
            || keyTail(statement).includes(needle)
            // the palette's search box honours the same abbreviations as the
            // typeahead, so "wpw" finds it in either place
            || shortcutScore(statement, needle) > 0);

    return STATEMENT_SECTION_ORDER
        .map(section => ({section, statements: visible.filter(statement => statement.section === section)}))
        .filter(group => group.statements.length > 0)
}
